import React, { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiUserCheck,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";
import ExpertApplicationDetails from "./expertApplicationDetails";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  Approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  Rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const ExpertApplications = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : "ar-EG";
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { id, name, action: 'confirm' | 'reject' }

  const STATUS_LABELS = {
    Pending: t("dashboard.expertApplications.common.status.pending"),
    Approved: t("dashboard.expertApplications.common.status.approved"),
    Rejected: t("dashboard.expertApplications.common.status.rejected"),
  };

  const stats = useMemo(
    () => ({
      total: applications.length,
      pending: applications.filter((a) => a.status === "Pending").length,
      approved: applications.filter((a) => a.status === "Approved").length,
      rejected: applications.filter((a) => a.status === "Rejected").length,
    }),
    [applications]
  );

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get("expert-applications/get-all");
      setApplications(response.data.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          t("dashboard.expertApplications.list.toast.loadError")
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchApplications();
  };

  const filteredApplications = useMemo(() => {
    return applications.filter((app) => {
      const matchesSearch =
        app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.number?.includes(searchTerm) ||
        app.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || app.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [applications, searchTerm, statusFilter]);

  const runAction = async () => {
    if (!confirmAction) return;
    const { id, action } = confirmAction;
    setActioningId(id);
    try {
      await adminAxios.patch(`expert-applications/${action}`, null, {
        params: { application_id: id },
      });
      toast.success(
        action === "confirm"
          ? t("dashboard.expertApplications.list.toast.approveSuccess")
          : t("dashboard.expertApplications.list.toast.rejectSuccess")
      );
      setConfirmAction(null);
      setSelected(null);
      fetchApplications();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          t("dashboard.expertApplications.list.toast.actionError")
      );
    } finally {
      setActioningId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.expertApplications.list.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.expertApplications.list.subtitle")}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="mt-4 lg:mt-0 flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          <span>{t("dashboard.expertApplications.list.refresh")}</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold">{stats.pending}</div>
          <div className="text-lg opacity-90">
            {t("dashboard.expertApplications.common.status.pending")}
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold">{stats.approved}</div>
          <div className="text-lg opacity-90">
            {t("dashboard.expertApplications.common.status.approved")}
          </div>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold">{stats.rejected}</div>
          <div className="text-lg opacity-90">
            {t("dashboard.expertApplications.common.status.rejected")}
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t("dashboard.expertApplications.list.search.placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        >
          <option value="all">
            {t("dashboard.expertApplications.list.filter.allStatuses")}
          </option>
          {Object.keys(STATUS_LABELS).map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.list.table.id")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.common.fields.name")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.common.fields.phone")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.common.fields.categories")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.list.table.status")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.common.fields.submittedAt")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.expertApplications.list.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {filteredApplications.map((app) => (
                <tr
                  key={app.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-left rtl:text-right">
                    #{app.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left rtl:text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {app.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {app.email || ""}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-left rtl:text-right">
                    {app.number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left rtl:text-right">
                    <div className="flex flex-wrap gap-1 justify-end">
                      {app.categories?.slice(0, 2).map((c) => (
                        <span
                          key={c.id}
                          className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                        >
                          {c.category?.name}
                        </span>
                      ))}
                      {app.categories?.length > 2 && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          +{app.categories.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left rtl:text-right">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        STATUS_COLORS[app.status] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {STATUS_LABELS[app.status] || app.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-left rtl:text-right">
                    {new Date(app.createdAt).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setSelected(app)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors px-2 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        title={t("dashboard.expertApplications.list.actions.view")}
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      {app.status === "Pending" && (
                        <>
                          <button
                            onClick={() =>
                              setConfirmAction({
                                id: app.id,
                                name: app.name,
                                action: "confirm",
                              })
                            }
                            disabled={actioningId === app.id}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 transition-colors px-2 py-1 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                            title={t("dashboard.expertApplications.list.actions.approve")}
                          >
                            <FiCheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              setConfirmAction({
                                id: app.id,
                                name: app.name,
                                action: "reject",
                              })
                            }
                            disabled={actioningId === app.id}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                            title={t("dashboard.expertApplications.list.actions.reject")}
                          >
                            <FiXCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredApplications.length === 0 && (
          <div className="text-center py-12">
            <FiUserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t("dashboard.expertApplications.list.emptyState.title")}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter !== "all"
                ? t("dashboard.expertApplications.list.emptyState.filtered")
                : t("dashboard.expertApplications.list.emptyState.empty")}
            </p>
          </div>
        )}
      </div>

      <ExpertApplicationDetails
        application={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        statusLabels={STATUS_LABELS}
        statusColors={STATUS_COLORS}
        onConfirm={(id, name) =>
          setConfirmAction({ id, name, action: "confirm" })
        }
        onReject={(id, name) =>
          setConfirmAction({ id, name, action: "reject" })
        }
      />

      {/* Confirm / Reject dialog */}
      {confirmAction && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setConfirmAction(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                {confirmAction.action === "confirm"
                  ? t("dashboard.expertApplications.list.confirmDialog.approveTitle")
                  : t("dashboard.expertApplications.list.confirmDialog.rejectTitle")}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {confirmAction.action === "confirm"
                  ? t("dashboard.expertApplications.list.confirmDialog.approveQuestion")
                  : t("dashboard.expertApplications.list.confirmDialog.rejectQuestion")}{" "}
                <strong>{confirmAction.name}</strong>
                {t("dashboard.expertApplications.list.confirmDialog.questionSuffix")}
                {confirmAction.action === "confirm" &&
                  t("dashboard.expertApplications.list.confirmDialog.approveNote")}
              </p>
              <div className="flex justify-end space-x-3 rtl:space-x-reverse">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t("dashboard.expertApplications.list.confirmDialog.cancel")}
                </button>
                <button
                  onClick={runAction}
                  disabled={actioningId === confirmAction.id}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                    confirmAction.action === "confirm"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {actioningId === confirmAction.id
                    ? t("dashboard.expertApplications.list.confirmDialog.processing")
                    : confirmAction.action === "confirm"
                      ? t("dashboard.expertApplications.list.confirmDialog.confirmApprove")
                      : t("dashboard.expertApplications.list.confirmDialog.confirmReject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertApplications;
